<script setup>
/* global axios */
// Detalhe do Projeto — abas: Tarefas, Categorias, Membros, Conversas, Arquivos.
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import KanbanMultiSelect from './KanbanMultiSelect.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const projectId = route.params.projectId;
const base = `/api/v1/accounts/${accountId}/kanban`;
const pbase = `${base}/projects/${projectId}`;

const project = ref(null);
const loading = ref(true);
const tab = ref('tasks');
const agents = ref([]);

const TABS = computed(() => [
  { key: 'tasks', label: t('PROJECTS.detail.tabs.tasks'), icon: 'i-lucide-check-square' },
  { key: 'milestones', label: t('PROJECTS.detail.tabs.milestones'), icon: 'i-lucide-tag' },
  { key: 'members', label: t('PROJECTS.detail.tabs.members'), icon: 'i-lucide-users' },
  { key: 'conversations', label: t('PROJECTS.detail.tabs.conversations'), icon: 'i-lucide-message-square' },
  { key: 'files', label: t('PROJECTS.detail.tabs.files'), icon: 'i-lucide-paperclip' },
]);
const STATUS = computed(() => ({
  active: t('PROJECTS.status.active'),
  completed: t('PROJECTS.status.completed'),
  cancelled: t('PROJECTS.status.cancelled'),
}));
const STATUS_CLS = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  completed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11',
};
const TASK_STATUS = computed(() => ({
  pending: t('PROJECTS.detail.taskStatus.pending'),
  in_progress: t('PROJECTS.detail.taskStatus.inProgress'),
  completed: t('PROJECTS.detail.taskStatus.completed'),
}));
const PRIORITY = computed(() => ({
  low: t('PROJECTS.detail.priority.low'),
  medium: t('PROJECTS.detail.priority.medium'),
  high: t('PROJECTS.detail.priority.high'),
  urgent: t('PROJECTS.detail.priority.urgent'),
}));
const PRIORITY_CLS = {
  low: 'bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const load = async () => {
  loading.value = true;
  project.value = (await axios.get(pbase)).data.data;
  loading.value = false;
};
const loadAgents = async () => {
  try {
    agents.value =
      (await axios.get(`/api/v1/accounts/${accountId}/agents`)).data || [];
  } catch (e) {
    agents.value = [];
  }
};

// ---- Tarefas (modal) ----
const showTaskModal = ref(false);
const newTask = ref({
  title: '',
  priority: '',
  due_date: '',
  milestone_id: '',
  assigned_to: '',
});
const openTaskModal = () => {
  newTask.value = {
    title: '',
    priority: '',
    due_date: '',
    milestone_id: '',
    assigned_to: '',
  };
  showTaskModal.value = true;
};
const addTask = async () => {
  if (!newTask.value.title.trim()) return;
  const payload = { ...newTask.value };
  Object.keys(payload).forEach(k => {
    if (payload[k] === '') delete payload[k];
  });
  await axios.post(`${pbase}/tasks`, payload);
  showTaskModal.value = false;
  await load();
};
const cycleTaskStatus = async task => {
  const order = ['pending', 'in_progress', 'completed'];
  const next = order[(order.indexOf(task.status) + 1) % order.length];
  await axios.patch(`${pbase}/tasks/${task.id}`, { status: next });
  await load();
};
const removeTask = async task => {
  await axios.delete(`${pbase}/tasks/${task.id}`);
  await load();
};

// ---- Categorias (milestones) ----
const newMilestone = ref({ name: '', due_date: '' });
const addMilestone = async () => {
  if (!newMilestone.value.name.trim()) return;
  const payload = { ...newMilestone.value };
  if (!payload.due_date) delete payload.due_date;
  await axios.post(`${pbase}/milestones`, payload);
  newMilestone.value = { name: '', due_date: '' };
  await load();
};
const removeMilestone = async m => {
  await axios.delete(`${pbase}/milestones/${m.id}`);
  await load();
};

// ---- Membros (modal) ----
const showMemberModal = ref(false);
const memberIds = ref([]);
const availableAgents = computed(() => {
  const ids = (project.value?.members || []).map(m => m.user_id);
  return agents.value.filter(a => !ids.includes(a.id));
});
const memberOptions = computed(() =>
  availableAgents.value.map(a => ({
    value: a.id,
    label: a.email ? `${a.name} · ${a.email}` : a.name,
  }))
);
const openMemberModal = () => {
  memberIds.value = [];
  showMemberModal.value = true;
};
const addMembers = async () => {
  if (!memberIds.value.length) return;
  await Promise.all(
    memberIds.value.map(id =>
      axios.post(`${pbase}/members`, { user_id: id }).catch(() => {})
    )
  );
  showMemberModal.value = false;
  memberIds.value = [];
  await load();
};
const removeMember = async m => {
  await axios.delete(`${pbase}/members/${m.id}`);
  await load();
};

// ---- Conversas ----
const newConvId = ref('');
const addConversation = async () => {
  if (!newConvId.value) return;
  await axios.post(`${pbase}/conversations`, {
    conversation_id: newConvId.value,
  });
  newConvId.value = '';
  await load();
};
const removeConversation = async c => {
  await axios.delete(`${pbase}/conversations/${c.id}`);
  await load();
};
const openConversation = c =>
  router.push(`/app/accounts/${accountId}/conversations/${c.conversation_id}`);

// ---- Arquivos ----
const fileInput = ref(null);
const uploading = ref(false);
const pickFile = () => fileInput.value?.click();
const uploadFile = async e => {
  const file = e.target.files[0];
  if (!file) return;
  uploading.value = true;
  try {
    const fd = new FormData();
    fd.append('file', file);
    await axios.post(`${pbase}/files`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  } finally {
    uploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
  await load();
};
const removeFile = async f => {
  await axios.delete(`${pbase}/files/${f.id}`);
  await load();
};

const goBack = () => router.push(`/app/accounts/${accountId}/projects`);
const fmtDate = d => (d ? new Date(d).toLocaleDateString('pt-BR') : '');

onMounted(() => {
  load();
  loadAgents();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-n-background">
    <div v-if="loading" class="p-8 text-n-slate-11">
      {{ t('PROJECTS.common.loadingShort') }}
    </div>
    <template v-else-if="project">
      <!-- header (identidade Chatwoot, sem fundo colorido) -->
      <div class="sticky top-0 z-10 bg-n-background border-b border-n-weak">
        <div class="flex items-center h-16 gap-3 px-6">
          <button
            class="inline-flex items-center gap-1.5 px-3 h-9 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="goBack"
          >
            <span class="i-lucide-chevron-left size-4" />
            {{ t('PROJECTS.detail.back') }}
          </button>
          <span
            class="w-3.5 h-3.5 rounded-full shrink-0"
            :style="{ background: project.color || '#3b82f6' }"
          />
          <h1 class="text-heading-1 text-n-slate-12 truncate">
            {{ project.name }}
          </h1>
          <span
            class="px-2 py-0.5 text-[0.6875rem] rounded-full shrink-0"
            :class="STATUS_CLS[project.status]"
            >{{ STATUS[project.status] }}</span
          >
          <div class="ml-auto flex items-center gap-4 shrink-0">
            <span
              v-if="project.deadline"
              class="inline-flex items-center gap-1 text-sm text-n-slate-11"
            >
              <span class="i-lucide-calendar size-4" />
              {{ fmtDate(project.deadline) }}</span
            >
            <div class="flex items-center gap-2 w-40">
              <div class="flex-1 h-1.5 rounded-full bg-n-alpha-2">
                <div
                  class="h-1.5 rounded-full bg-n-brand"
                  :style="{ width: project.progress + '%' }"
                />
              </div>
              <span class="text-[0.6875rem] text-n-slate-10 w-8"
                >{{ project.progress }}%</span
              >
            </div>
          </div>
        </div>
        <p
          v-if="project.description"
          class="px-6 pb-3 -mt-1 text-sm text-n-slate-10 truncate"
        >
          {{ project.description }}
        </p>

        <!-- tabs -->
        <div class="flex gap-1 px-6">
          <button
            v-for="tb in TABS"
            :key="tb.key"
            class="flex items-center gap-1.5 px-3.5 py-3 text-sm text-n-slate-10 border-b-2 border-transparent"
            :class="{
              'text-n-brand border-n-brand font-semibold': tab === tb.key,
            }"
            @click="tab = tb.key"
          >
            <span :class="tb.icon" class="size-4" />
            {{ tb.label }}
            <span
              class="text-xs px-1.5 rounded-full bg-n-alpha-2 text-n-slate-11"
              >{{
                tb.key === 'tasks'
                  ? project.tasks.length
                  : tb.key === 'milestones'
                    ? project.milestones.length
                    : tb.key === 'members'
                      ? project.members.length
                      : tb.key === 'conversations'
                        ? project.conversations.length
                        : project.files.length
              }}</span
            >
          </button>
        </div>
      </div>

      <div class="max-w-4xl px-6 py-6 mx-auto w-full">
        <!-- TAREFAS -->
        <div v-if="tab === 'tasks'">
          <div class="flex justify-end mb-4">
            <button
              class="inline-flex items-center gap-1.5 px-3.5 h-9 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
              @click="openTaskModal"
            >
              <span class="i-lucide-plus size-4" />
              {{ t('PROJECTS.detail.tasks.newTask') }}
            </button>
          </div>

          <div
            v-if="!project.tasks.length"
            class="py-8 text-center text-n-slate-10"
          >
            {{ t('PROJECTS.detail.tasks.empty') }}
          </div>
          <div
            v-for="task in project.tasks"
            :key="task.id"
            class="flex items-center gap-2.5 p-2.5 mb-1.5 rounded-lg border border-n-weak bg-n-solid-1 text-sm text-n-slate-12"
          >
            <button
              class="flex items-center justify-center w-[22px] h-[22px] rounded-md border-[1.5px] border-n-weak bg-n-background text-n-blue-11"
              :class="{
                'bg-n-teal-9 border-n-teal-9 text-white':
                  task.status === 'completed',
              }"
              @click="cycleTaskStatus(task)"
            >
              <span
                v-if="task.status === 'completed'"
                class="i-lucide-check size-3.5"
              />
              <span
                v-else-if="task.status === 'in_progress'"
                class="i-lucide-clock size-3.5"
              />
            </button>
            <span
              class="flex-1"
              :class="{
                'line-through text-n-slate-9': task.status === 'completed',
              }"
              >{{ task.title }}</span
            >
            <span
              v-if="task.priority"
              class="px-2 py-0.5 text-[0.625rem] rounded-full"
              :class="PRIORITY_CLS[task.priority]"
              >{{ PRIORITY[task.priority] }}</span
            >
            <span class="text-[0.6875rem] text-n-slate-10">{{
              TASK_STATUS[task.status]
            }}</span>
            <span
              v-if="task.assignee_name"
              class="inline-flex items-center gap-1 text-[0.6875rem] text-n-slate-10"
            >
              <span class="i-lucide-user size-3.5" />
              {{ task.assignee_name }}</span
            >
            <span
              v-if="task.due_date"
              class="inline-flex items-center gap-1 text-[0.6875rem] text-n-slate-10"
            >
              <span class="i-lucide-calendar size-3.5" />
              {{ fmtDate(task.due_date) }}</span
            >
            <button
              class="inline-flex items-center justify-center size-7 rounded-md text-n-ruby-11 hover:bg-n-alpha-1"
              @click="removeTask(task)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>

        <!-- CATEGORIAS -->
        <div v-else-if="tab === 'milestones'">
          <div class="flex gap-2 mb-4">
            <input
              v-model="newMilestone.name"
              class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              :placeholder="t('PROJECTS.detail.milestones.placeholder')"
              @keyup.enter="addMilestone"
            />
            <input
              v-model="newMilestone.due_date"
              type="date"
              class="w-[150px] px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
            <button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
              @click="addMilestone"
            >
              {{ t('PROJECTS.common.add') }}
            </button>
          </div>
          <div
            v-if="!project.milestones.length"
            class="py-8 text-center text-n-slate-10"
          >
            {{ t('PROJECTS.detail.milestones.empty') }}
          </div>
          <div
            v-for="m in project.milestones"
            :key="m.id"
            class="flex items-center gap-2.5 p-2.5 mb-1.5 rounded-lg border border-n-weak bg-n-solid-1 text-sm text-n-slate-12"
          >
            <span
              class="inline-flex items-center flex-1 gap-1.5 font-medium text-n-slate-12"
            >
              <span class="i-lucide-tag size-4 text-n-slate-10" />
              {{ m.name }}</span
            >
            <span class="text-[0.6875rem] text-n-slate-10">{{
              t('PROJECTS.detail.milestones.tasksCount', {
                done: m.done_count,
                total: m.task_count,
              })
            }}</span>
            <span
              v-if="m.due_date"
              class="inline-flex items-center gap-1 text-[0.6875rem] text-n-slate-10"
            >
              <span class="i-lucide-calendar size-3.5" />
              {{ fmtDate(m.due_date) }}</span
            >
            <button
              class="inline-flex items-center justify-center size-7 rounded-md text-n-ruby-11 hover:bg-n-alpha-1"
              @click="removeMilestone(m)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>

        <!-- MEMBROS -->
        <div v-else-if="tab === 'members'">
          <div class="flex justify-end mb-4">
            <button
              class="inline-flex items-center gap-1.5 px-3.5 h-9 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
              @click="openMemberModal"
            >
              <span class="i-lucide-user-plus size-4" />
              {{ t('PROJECTS.detail.members.addAgent') }}
            </button>
          </div>
          <div
            v-if="!project.members.length"
            class="py-8 text-center text-n-slate-10"
          >
            {{ t('PROJECTS.detail.members.empty') }}
          </div>
          <div
            v-for="m in project.members"
            :key="m.id"
            class="flex items-center gap-2.5 p-2.5 mb-1.5 rounded-lg border border-n-weak bg-n-solid-1 text-sm text-n-slate-12"
          >
            <span
              class="flex items-center justify-center size-8 text-xs font-semibold text-white rounded-full bg-n-brand shrink-0"
              >{{ (m.name || '?')[0].toUpperCase() }}</span
            >
            <div class="flex-1 min-w-0">
              <div class="text-n-slate-12 truncate">{{ m.name }}</div>
              <div class="text-[0.6875rem] text-n-slate-10 truncate">
                {{ m.email }}
              </div>
            </div>
            <span
              class="px-2 py-0.5 text-[0.625rem] rounded-full shrink-0"
              :class="
                m.role === 'owner'
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11'
              "
              >{{
                m.role === 'owner'
                  ? t('PROJECTS.detail.members.roleOwner')
                  : t('PROJECTS.detail.members.roleMember')
              }}</span
            >
            <button
              v-if="m.role !== 'owner'"
              class="inline-flex items-center justify-center size-7 rounded-md text-n-ruby-11 hover:bg-n-alpha-1 shrink-0"
              @click="removeMember(m)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>

        <!-- CONVERSAS -->
        <div v-else-if="tab === 'conversations'">
          <div class="flex gap-2 mb-4">
            <input
              v-model="newConvId"
              type="number"
              class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              :placeholder="t('PROJECTS.detail.conversations.placeholder')"
              @keyup.enter="addConversation"
            />
            <button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
              @click="addConversation"
            >
              {{ t('PROJECTS.detail.conversations.link') }}
            </button>
          </div>
          <div
            v-if="!project.conversations.length"
            class="py-8 text-center text-n-slate-10"
          >
            {{ t('PROJECTS.detail.conversations.empty') }}
          </div>
          <div
            v-for="c in project.conversations"
            :key="c.id"
            class="flex items-center gap-2.5 p-2.5 mb-1.5 rounded-lg border border-n-weak bg-n-solid-1 text-sm text-n-slate-12"
          >
            <span class="inline-flex items-center flex-1 gap-1.5 text-n-slate-12">
              <span class="i-lucide-message-square size-4 text-n-slate-10" />
              {{
                t('PROJECTS.detail.conversations.itemLabel', {
                  id: c.display_id,
                  contact:
                    c.contact_name ||
                    t('PROJECTS.detail.conversations.contactFallback'),
                })
              }}</span
            >
            <span v-if="c.inbox_name" class="text-[0.6875rem] text-n-slate-10">{{
              c.inbox_name
            }}</span>
            <span
              class="px-2 py-0.5 text-[0.625rem] rounded-full bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11"
              >{{ c.status }}</span
            >
            <button
              class="inline-flex items-center gap-1.5 px-3 h-8 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
              @click="openConversation(c)"
            >
              {{ t('PROJECTS.common.open') }}
            </button>
            <button
              class="inline-flex items-center justify-center size-7 rounded-md text-n-ruby-11 hover:bg-n-alpha-1"
              @click="removeConversation(c)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>

        <!-- ARQUIVOS -->
        <div v-else-if="tab === 'files'">
          <input
            ref="fileInput"
            type="file"
            class="hidden"
            @change="uploadFile"
          />
          <button
            class="flex flex-col items-center justify-center w-full gap-2 py-8 mb-4 text-sm border-2 border-dashed rounded-xl border-n-weak text-n-slate-10 hover:border-n-brand hover:text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-60"
            :disabled="uploading"
            @click="pickFile"
          >
            <span class="i-lucide-upload-cloud size-6" />
            <span>{{
              uploading
                ? t('PROJECTS.detail.files.uploading')
                : t('PROJECTS.detail.files.pick')
            }}</span>
          </button>
          <div
            v-if="!project.files.length"
            class="py-8 text-center text-n-slate-10"
          >
            {{ t('PROJECTS.detail.files.empty') }}
          </div>
          <div
            v-for="f in project.files"
            :key="f.id"
            class="flex items-center gap-2.5 p-2.5 mb-1.5 rounded-lg border border-n-weak bg-n-solid-1 text-sm text-n-slate-12"
          >
            <a
              :href="f.url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center flex-1 gap-1.5 text-n-blue-11 hover:underline truncate"
            >
              <span class="i-lucide-paperclip size-4 shrink-0" />
              <span class="truncate">{{ f.filename }}</span></a
            >
            <span class="text-[0.6875rem] text-n-slate-10 shrink-0">{{
              t('PROJECTS.detail.files.size', {
                size: Math.round((f.byte_size || 0) / 1024),
              })
            }}</span>
            <button
              class="inline-flex items-center justify-center size-7 rounded-md text-n-ruby-11 hover:bg-n-alpha-1 shrink-0"
              @click="removeFile(f)"
            >
              <span class="i-lucide-trash-2 size-4" />
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- modal Nova Tarefa -->
    <div
      v-if="showTaskModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showTaskModal = false"
    >
      <div class="bg-n-background rounded-xl shadow-xl w-[440px] max-w-[92vw] p-5">
        <h3 class="text-heading-3 mb-4 text-n-slate-12">
          {{ t('PROJECTS.detail.tasks.newTask') }}
        </h3>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('PROJECTS.detail.tasks.titleLabel')
        }}</label>
        <input
          v-model="newTask.title"
          class="w-full mb-3 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          :placeholder="t('PROJECTS.detail.tasks.placeholder')"
          @keyup.enter="addTask"
        />
        <div class="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('PROJECTS.detail.tasks.priority')
            }}</label>
            <select
              v-model="newTask.priority"
              class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            >
              <option value="">—</option>
              <option v-for="(l, k) in PRIORITY" :key="k" :value="k">
                {{ l }}
              </option>
            </select>
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('PROJECTS.detail.tasks.dueDate')
            }}</label>
            <input
              v-model="newTask.due_date"
              type="date"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
          </div>
          <div v-if="project && project.milestones.length">
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('PROJECTS.detail.tabs.milestones')
            }}</label>
            <select
              v-model="newTask.milestone_id"
              class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            >
              <option value="">
                {{ t('PROJECTS.detail.tasks.noMilestone') }}
              </option>
              <option v-for="m in project.milestones" :key="m.id" :value="m.id">
                {{ m.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('PROJECTS.detail.tasks.assignee')
            }}</label>
            <select
              v-model="newTask.assigned_to"
              class="w-full pl-3 pr-9 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            >
              <option value="">—</option>
              <option v-for="a in agents" :key="a.id" :value="a.id">
                {{ a.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="flex justify-end gap-2">
          <button
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="showTaskModal = false"
          >
            {{ t('PROJECTS.modal.cancel') }}
          </button>
          <button
            class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
            :disabled="!newTask.title.trim()"
            @click="addTask"
          >
            {{ t('PROJECTS.common.add') }}
          </button>
        </div>
      </div>
    </div>

    <!-- modal Adicionar Membros -->
    <div
      v-if="showMemberModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="showMemberModal = false"
    >
      <div class="bg-n-background rounded-xl shadow-xl w-[440px] max-w-[92vw] p-5">
        <h3 class="text-heading-3 mb-4 text-n-slate-12">
          {{ t('PROJECTS.detail.members.addAgent') }}
        </h3>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
          t('PROJECTS.detail.members.selectAgents')
        }}</label>
        <KanbanMultiSelect
          v-model="memberIds"
          :options="memberOptions"
          :placeholder="t('PROJECTS.detail.members.addAgent')"
        />
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
            @click="showMemberModal = false"
          >
            {{ t('PROJECTS.modal.cancel') }}
          </button>
          <button
            class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
            :disabled="!memberIds.length"
            @click="addMembers"
          >
            {{ t('PROJECTS.common.add') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
